import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';

export type RunStatus = 'running' | 'completed' | 'failed';
export type OrgRunState = 'pending' | 'completed' | 'skipped' | 'failed';

export interface OrgRunStatus {
  orgId: string;
  orgName: string;
  state: OrgRunState;
  error?: string;
}

export interface MigrationRunEvent {
  id: number;
  ts: string;
  level: string;
  kind: string;
  message: string;
  orgId?: string;
}

export interface MigrationRun {
  runId: string;
  status: RunStatus;
  startedAt: string;
  completedAt?: string;
  orgStatuses: OrgRunStatus[];
  pollError?: boolean;
  error?: string;
  checkpointSummary?: string;
  lastCopyFailure?: string;
  lastInvoke?: string;
  hasPartialErrors?: boolean;
  events?: MigrationRunEvent[];
}

interface MigrationRunPanelProps {
  runs: MigrationRun[];
  busyRunId?: string | null;
  onResume?: (runId: string) => void;
  onSkipOrg?: (runId: string) => void;
  onRetryOrg?: (runId: string) => void;
}

const RUN_STATUS_BADGE: Record<RunStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' }> = {
  running:   { label: 'Running',   variant: 'default' },
  completed: { label: 'Completed', variant: 'secondary' },
  failed:    { label: 'Failed',    variant: 'destructive' },
};

function fmt(iso: string) {
  return new Date(iso).toLocaleString();
}

function copyText(value: string): void {
  void navigator.clipboard?.writeText(value);
}

function eventsToNdjson(events: MigrationRunEvent[]): string {
  return events.map(event => JSON.stringify(event)).join('\n');
}

function downloadNdjson(runId: string, events: MigrationRunEvent[]): void {
  const blob = new Blob([eventsToNdjson(events)], { type: 'application/x-ndjson' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `migration-run-${runId}.ndjson`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function MigrationRunPanel({
  runs, busyRunId, onResume, onSkipOrg, onRetryOrg,
}: MigrationRunPanelProps) {
  const [copiedRunId, setCopiedRunId] = useState<string | null>(null);

  if (runs.length === 0) {
    return (
      <Card>
        <CardHeader><CardTitle>Run Panel</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">
            No active run. Select organisations and click "Start migration".
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Migration Runs</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {runs.map(run => {
          const hasErrors = Boolean(
            run.hasPartialErrors
            || run.error
            || run.orgStatuses.some(org => Boolean(org.error) || org.state === 'failed'),
          );
          const badge = run.status === 'completed' && hasErrors
            ? { label: 'Completed with errors', variant: 'destructive' as const }
            : RUN_STATUS_BADGE[run.status];
          const canResume = run.status === 'failed'
            || run.status === 'running'
            || (run.status === 'completed' && hasErrors);
          const events = run.events ?? [];
          const visibleEvents = events.slice(-100);

          return (
            <div key={run.runId} className="space-y-2 border-b border-gray-100 pb-4 last:border-0 last:pb-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <Badge variant={badge.variant}>{badge.label}</Badge>
                {run.pollError && (
                  <span className="text-xs text-yellow-700">Lost connection — check status manually.</span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                <span>Run ID: {run.runId}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 px-2"
                  onClick={() => {
                    copyText(run.runId);
                    setCopiedRunId(run.runId);
                  }}
                >
                  {copiedRunId === run.runId ? 'Copied' : 'Copy ID'}
                </Button>
              </div>
              {run.checkpointSummary && (
                <p className="text-xs font-mono text-gray-600">{run.checkpointSummary}</p>
              )}
              {run.error && (
                <p className="text-sm text-red-700">Run error: {run.error}</p>
              )}
              {run.lastCopyFailure && (
                <p className="text-sm text-red-700">Last copy failure: {run.lastCopyFailure}</p>
              )}
              {run.lastInvoke && (
                <p className="text-sm text-red-700">Last worker invoke: {run.lastInvoke}</p>
              )}
              {run.orgStatuses.length === 0 ? (
                <p className="text-sm text-gray-400">No organisations in this run.</p>
              ) : (
                run.orgStatuses.map(org => {
                  const isOrgDone = org.state === 'completed' || org.state === 'skipped' || org.state === 'failed';
                  return (
                    <p key={org.orgId} className="text-sm text-gray-700">
                      Organisation {org.orgName} status: {org.state}, started at: {fmt(run.startedAt)}
                      {isOrgDone && run.completedAt ? `, completed at: ${fmt(run.completedAt)}` : ''}
                      {org.error ? ` — ${org.error}` : ''}
                    </p>
                  );
                })
              )}
              {visibleEvents.length > 0 && (
                <div className="space-y-1">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => copyText(events.map(event => event.message).join('\n'))}
                    >
                      Copy logs
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => downloadNdjson(run.runId, events)}
                    >
                      Download NDJSON
                    </Button>
                  </div>
                  <ScrollArea className="h-40 rounded-md border bg-gray-50 p-2">
                    <pre className="text-xs whitespace-pre-wrap">
                      {visibleEvents.map(event => `${event.ts} [${event.level}] ${event.message}`).join('\n')}
                    </pre>
                  </ScrollArea>
                </div>
              )}
              {canResume && (
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button
                    type="button"
                    size="sm"
                    disabled={busyRunId === run.runId}
                    onClick={() => onResume?.(run.runId)}
                  >
                    Resume
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busyRunId === run.runId}
                    onClick={() => onSkipOrg?.(run.runId)}
                  >
                    Skip current org
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busyRunId === run.runId}
                    onClick={() => onRetryOrg?.(run.runId)}
                  >
                    Retry current org
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
